/**
 * React-Lit bridge utilities for BlockSuite integration
 * Adapted from AFFiNE's implementation
 */

import React, { useRef, useLayoutEffect, forwardRef } from 'react'

interface CreateComponentOptions<T extends HTMLElement> {
  tagName: string
  elementClass?: new () => T
  displayName?: string
}

type PropsWithContainer<P> = P & {
  containerStyle?: React.CSSProperties
  containerClassName?: string
}

/**
 * Creates a React component from a custom HTML element class
 * This avoids direct instantiation and uses the browser's custom element registry
 */
export function createReactComponentFromLit<T extends HTMLElement, P = Record<string, any>>({
  tagName,
  elementClass,
  displayName
}: CreateComponentOptions<T>) {
  
  const ReactComponent = forwardRef<T, PropsWithContainer<P> & React.HTMLAttributes<T>>((props, ref) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const elementRef = useRef<T | null>(null)
    
    // Create element and apply props in a single effect to ensure proper timing
    useLayoutEffect(() => {
      const container = containerRef.current
      if (!container) return
      
      // Create element if it doesn't exist
      if (elementRef.current === null) {
        const element = document.createElement(tagName) as T
        elementRef.current = element
        
        // Set ref if provided
        if (ref) {
          if (typeof ref === 'function') {
            ref(element)
          } else {
            ref.current = element
          }
        }
      }
      
      const element = elementRef.current
      if (!element) return
      
      // Apply all props BEFORE connecting to DOM
      Object.keys(props).forEach(key => {
        if (key === 'children' || key === 'containerStyle' || key === 'containerClassName') return
        
        const value = (props as any)[key]
        if (value !== undefined && value !== null) {
          try {
            // Handle different prop types
            if (typeof value === 'function') {
              // Event handlers
              if (key.startsWith('on')) {
                const eventName = key.slice(2).toLowerCase()
                element.addEventListener(eventName, value as EventListener)
              }
            } else {
              // Properties and attributes
              // Set all properties immediately (don't wait for doc.ready)
              ;(element as any)[key] = value
            }
          } catch (error) {
            console.warn(`Failed to set property ${key} on ${tagName}:`, error)
          }
        }
      })
      
      // Only append to DOM after all props are set
      if (!element.isConnected) {
        container.innerHTML = ''
        container.appendChild(element)
      }
      
      return () => {
        if (element.isConnected && element.parentNode === container) {
          container.removeChild(element)
        }
      }
    })
    
    const { containerStyle, containerClassName, ...restProps } = props
    
    return React.createElement('div', {
      ref: containerRef,
      style: {
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        ...containerStyle
      },
      className: containerClassName || 'blocksuite-editor-container'
    })
  })
  
  ReactComponent.displayName = displayName || `LitReact(${tagName})`
  
  return ReactComponent
}